/**
 * ADR-174 §3 — how a change-log row names its contract.
 *
 * The server serves FACTS (`row.contract`: number, type, company + country,
 * period, isPrimary, concurrent, ownership, source); the label is built ONCE,
 * by contractIdentityLabel (ADR-113/116/148) — never restated here. This module
 * only adapts the block to the episode shape that formatter reads, and words
 * the ownership the detail line no longer carries (Q6 — Option A: the label
 * takes the slot, ownership moves to the tooltip and the expanded panel).
 */
import { contractIdentityLabel } from '../../../constants/contractActions';

/** The contract block in the episode shape contractIdentityLabel reads. */
export const contractAsEpisode = (contract) => (contract
  ? {
    id: contract.episodeId,
    startDate: contract.startDate || null,
    endDate: contract.endDate || null,
    contractType: contract.contractType || null,
    contractCompany: contract.contractCompany || null,
    payload: contract.number ? { sourceReference: { contractId: contract.number } } : null,
  }
  : null);

/**
 * The contract's identity: `Contract · 12345444 — Supplier — Software Mind
 * S.R.L. (Moldova)`, else `B2B — Software Mind CF S.R.L. (Romania) — from
 * 16/02/2026`. Null for a person fact (no contract).
 */
export const contractLabel = (contract) => {
  if (!contract) return null;
  return contractIdentityLabel(null, contractAsEpisode(contract)) || 'Contract';
};

/** Ownership, in words — the tooltip and the expanded panel (§3.1). */
export const ownershipLabel = (contract) => {
  if (!contract) return null;
  if (contract.ownership === 'sync') {
    return contract.source ? `Sync-stamped by ${contract.source}` : 'Sync-stamped';
  }
  return 'Radar-owned';
};

/**
 * ADR-156 §2 — name the primary only when there is something to tell apart.
 * `isPrimary` is TODAY's pointer (radar keeps no pointer history), so the chip
 * is shown only on a contract that is still open (§10).
 */
export const showPrimaryChip = (contract) => Boolean(
  contract && contract.isPrimary && contract.concurrent && !contract.endDate
);

/** §3.3 — the "No contract number" hint: unnumbered, radar-owned. */
export const lacksNumber = (contract) => Boolean(
  contract && !contract.number && contract.ownership === 'radar'
);

/**
 * §4 — an amendment number is its CONTRACT'S own sequence, so it is always
 * qualified by the contract: `Amendment #5 to B2B — … (Romania) — from 16/02/2026`.
 */
export const amendmentHeading = (row) => {
  const n = row?.amendmentNumber != null ? ` #${row.amendmentNumber}` : '';
  const label = contractLabel(row?.contract);
  return label ? `Amendment${n} to ${label}` : `Amendment${n}`;
};
